import { useState } from "react";
import styles from "./Sidebar.module.css";
import logo from "@/assets/icons/logo-hector.svg";
import iconOverview from "@/assets/icons/nav-overview.svg";
import iconAvailability from "@/assets/icons/nav-availability.svg";
import iconVisibility from "@/assets/icons/nav-visibility.svg";
import iconDiscount from "@/assets/icons/nav-discount.svg";
import chevron from "@/assets/icons/chevron-closed.svg";

export interface NavChild {
  id: string;
  label: string;
  /** Unbuilt entries are labelled and disabled, never faked. */
  available?: boolean;
}
export interface NavGroup {
  /** Route section — the first hash segment. */
  id: string;
  label: string;
  icon: string;
  iconSize: 16 | 20;
  available?: boolean;
  children?: NavChild[];
}

/**
 * Nav tree exactly as authored in Figma 2178:32782.
 *
 * AUTHORED IN FULL, SHOWN IN PART. The build is focused on the three reports
 * brought over from reports.hectorai.live, so only those (and Settings, which
 * edits their plan) are listed. The original four groups are kept here rather
 * than deleted - they are working pages with live endpoints behind them, and
 * removing the tree would lose the Figma-authored order when they come back.
 * To restore one, add its id to VISIBLE_SECTIONS.
 */
const ALL_NAV: NavGroup[] = [
  {
    id: "overview",
    label: "Overview",
    icon: iconOverview,
    iconSize: 16,
    children: [
      { id: "sales-dashboard", label: "Sales Dashboard", available: true },
      { id: "actions", label: "Actions", available: true },
    ],
  },
  {
    id: "availability",
    label: "Availability",
    icon: iconAvailability,
    iconSize: 20,
    children: [
      { id: "supply-chain", label: "Supply Chain", available: true },
      { id: "po-modules", label: "PO Modules", available: true },
    ],
  },
  {
    id: "visibility",
    label: "Visibility",
    icon: iconVisibility,
    iconSize: 20,
    available: true,
  },
  {
    id: "discount",
    label: "Discount",
    icon: iconDiscount,
    iconSize: 20,
    available: true,
  },

  /**
   * The three published reports, brought in-app. Sub-pages mirror each
   * report's own left rail exactly, so someone who knows the report finds the
   * same sections in the same order here.
   */
  {
    id: "ceo",
    label: "CEO View",
    icon: iconOverview,
    iconSize: 16,
    children: [
      { id: "market-position", label: "Market position", available: true },
      { id: "competition", label: "Competition", available: true },
    ],
  },
  {
    id: "operations",
    label: "Operations",
    icon: iconAvailability,
    iconSize: 20,
    children: [
      { available: true, id: "control-tower", label: "Control tower" },
      { available: true, id: "cities", label: "Cities" },
      { available: true, id: "sub-lines", label: "Subline by City" },
      { available: true, id: "fill-rate", label: "Fill rate" },
    ],
  },
  {
    id: "marketing",
    label: "Marketing",
    icon: iconVisibility,
    iconSize: 20,
    children: [
      { available: true, id: "overview", label: "Overview" },
      { available: true, id: "skus", label: "SKUs" },
      { available: true, id: "actions", label: "Actions" },
      { available: true, id: "spend-structure", label: "Spend structure" },
      { available: true, id: "keywords-sov", label: "Keywords & SOV" },
      { available: true, id: "timing-budget", label: "Timing & budget" },
    ],
  },

  /**
   * Where the plan behind "Plan & forecast" is edited. It is a report input,
   * not a report, so it sits at the bottom rather than inside Operations.
   */
  {
    id: "settings",
    label: "Settings",
    icon: iconDiscount,
    iconSize: 20,
    children: [{ id: "targets", label: "Targets", available: true }],
  },
];

/**
 * The sections this build shows. Everything else stays authored above and
 * unrendered - hidden, not disabled: a greyed-out row invites a click and
 * then explains itself, which is worse than not being there.
 */
const VISIBLE_SECTIONS = new Set([
  "ceo",
  "operations",
  "marketing",
  "settings",
]);

const NAV: NavGroup[] = ALL_NAV.filter((g) => VISIBLE_SECTIONS.has(g.id));

/** The nav tree, so routing can validate a hash against what exists. */
export { NAV, ALL_NAV, VISIBLE_SECTIONS };

export function Sidebar({
  activeId = "supply-chain",
  activeSection,
  onNavigate,
}: {
  activeId?: string;
  activeSection?: string;
  onNavigate?: (section: string, page: string) => void;
}) {
  /**
   * Only the groups the USER has toggled. Whether a group is open is derived
   * below, not stored — writing the active section into state from an effect
   * cascades a second render on every navigation and fights the user's own
   * clicks, which is the same trap the availability selection avoids.
   */
  const [toggled, setToggled] = useState<Record<string, boolean>>({});
  const isOpen = (id: string): boolean =>
    toggled[id] ?? (id === activeSection || id === "ceo");
  const toggle = (id: string): void => {
    setToggled((p) => ({ ...p, [id]: !isOpen(id) }));
  };

  return (
    <nav className={styles.sidebar} aria-label="Primary">
      <img className={styles.logo} src={logo} alt="Hector" />
      <div className={styles.nav}>
        {NAV.map((group) => {
          const groupOpen = isOpen(group.id);
          const hasChildren = Boolean(group.children?.length);
          return (
            <div className={styles.group} key={group.id}>
              <button
                type="button"
                className={`${styles.item} ${hasChildren || group.available ? "" : styles.disabled}`}
                disabled={!hasChildren && !group.available}
                title={
                  hasChildren || group.available
                    ? group.label
                    : `${group.label} is not part of this build`
                }
                aria-expanded={hasChildren ? groupOpen : undefined}
                onClick={() =>
                  hasChildren ? toggle(group.id) : onNavigate?.(group.id, "")
                }
              >
                <span className={styles.itemInner}>
                  <span className={styles.itemLeft}>
                    <span className={styles.iconBox}>
                      <img
                        className={
                          group.iconSize === 16 ? styles.icon16 : styles.icon20
                        }
                        src={group.icon}
                        alt=""
                      />
                    </span>
                    <span className={styles.label}>{group.label}</span>
                  </span>
                  <img
                    className={`${styles.chevron} ${hasChildren && groupOpen ? styles.chevronOpen : ""}`}
                    src={chevron}
                    alt=""
                  />
                </span>
              </button>

              {hasChildren && groupOpen && (
                <div className={styles.children}>
                  <span className={styles.rail} aria-hidden="true" />
                  <div className={styles.childList}>
                    {(group.children ?? []).map((child) => (
                      <button
                        type="button"
                        key={child.id}
                        disabled={!child.available}
                        title={
                          child.available
                            ? child.label
                            : `${child.label} is not part of this build`
                        }
                        className={`${styles.item} ${child.id === activeId ? styles.active : ""} ${child.available ? "" : styles.disabled}`}
                        aria-current={
                          child.id === activeId ? "page" : undefined
                        }
                        aria-disabled={!child.available}
                        onClick={() => onNavigate?.(group.id, child.id)}
                      >
                        <span className={styles.itemInner}>
                          <span className={styles.itemLeft}>
                            <span className={styles.label}>{child.label}</span>
                          </span>
                          <img
                            className={styles.chevron}
                            src={chevron}
                            alt=""
                          />
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </nav>
  );
}
